import { useMemo } from 'react'
import { Link } from 'wouter'
import { Markdown } from '../components/Markdown'
import { BackdropOrbs } from '../components/BackdropOrbs'
import { SiteFooter } from '../components/SiteFooter'
import { loreToMarkdown } from '../lore/export'

/** The full-lore export page: a readable preview of the single Markdown document
 *  plus a download button. The document is generated from the live lore model, so
 *  it always matches what is on the site. */
export function Export() {
  const md = useMemo(() => loreToMarkdown(), [])

  const download = () => {
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'system-404.md'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(md)
    } catch {
      /* clipboard may be blocked; the download button is the reliable path */
    }
  }

  return (
    <div className="lorebook-root flex min-h-screen flex-col">
      <BackdropOrbs />
      <header className="sticky top-0 z-30 border-b border-edge bg-void/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <Link href="/lorebook" className="text-sm text-slate-300 transition hover:text-signal">
              ← Lorebook
            </Link>
            <span className="text-slate-700">/</span>
            <span className="font-display text-sm text-white">Export</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copy}
              className="rounded-full border border-edge px-4 py-1.5 text-sm text-slate-300 transition hover:border-signal/60 hover:text-white"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={download}
              className="rounded-full border border-signal/50 bg-signal/10 px-4 py-1.5 text-sm text-signal transition hover:bg-signal/20 hover:text-white"
            >
              Download .md
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 lg:px-6">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.35em] text-signal/70">Export</div>
          <h1 className="mt-2 font-display text-4xl font-semibold text-white">
            The whole lorebook, one file
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            Every world, creature, plant, and faction of System 404 as a single Markdown document.
            Take it with you, read it offline, drop it into your notes. Generated live from the
            lorebook so it is never out of date.
          </p>
        </div>

        <div className="export-doc rounded-2xl border border-edge bg-void-2/60 p-6 sm:p-8">
          <Markdown plain>{md}</Markdown>
        </div>
      </div>
      <SiteFooter />
    </div>
  )
}
